import ChevronRightIcon from '@assets/icons/chevron-right.svg';
import i18n from '@/utils/i18n';
import NoteIcon from '@assets/icons/note.svg';
import SendNoteIcon from '@assets/icons/send-note.svg';
import {
  getPartitionsForSkuInCollection,
  OrderGuideSearchItemInfo,
} from '@/hooks/use-search-item-order-guide-info';
import {
  ItemListOptionMetadata,
  OrderGuideCollection,
  OrderGuidePartition,
} from '@/contexts/item-context-types';
import { SearchOrderGuideSelectionActionSheet } from './search-order-guide-selection-action-sheet';
import { StyleSheet, View } from 'react-native';
import { useCallback } from 'react';
import { useCanWriteLineItemRequest } from '@/hooks/dynamic-config/use-can-write-line-item-request';
import { useGetOrderGuideItem } from '@/hooks/use-get-order-guide-item';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useTheme } from '@/contexts/theme-context';
import { useUpdateOrderGuideItemContext } from '@/contexts/update-order-guide-item-context';

interface SearchAddNoteActionSheetProps {
  info: OrderGuideSearchItemInfo;
  onClose: () => void;
  skusInOrderGuide: ItemListOptionMetadata[];
}

export const SearchAddNoteActionSheet = ({
  info,
  onClose,
  skusInOrderGuide,
}: SearchAddNoteActionSheetProps) => {
  const { editItemNotes } = useUpdateOrderGuideItemContext();
  const { collections } = useOrderGuideItemsContext();
  const getOrderGuideItem = useGetOrderGuideItem();
  const canWriteRequest = useCanWriteLineItemRequest();

  const theme = useTheme();

  const handleSelect = useCallback(
    (sku: ItemListOptionMetadata, partitionUUID?: string) => {
      if (!partitionUUID) {
        // should be called with partitionUUID, typescript check
        return;
      }

      const skusWithPartition = sku.skus
        .map((sku) => ({ ...sku, orderGuidePartitionUUID: partitionUUID }))
        .filter((sku) => getOrderGuideItem(sku) !== null);

      const clonedMetadata: ItemListOptionMetadata = {
        ...sku,
        orderGuidePartitionUUID: partitionUUID,
        skus: skusWithPartition,
      };

      editItemNotes(clonedMetadata);
    },
    [editItemNotes, getOrderGuideItem],
  );

  const renderCollectionRightComponent = useCallback(
    (collection: OrderGuideCollection, selectedSku: ItemListOptionMetadata) => {
      const partitions = getPartitionsForSkuInCollection(
        info,
        selectedSku,
        collection.uuid,
        collections,
      );

      const hasNotes = partitions.some((p) => p.hasNotes);
      const hasRequests = partitions.some((p) => p.hasRequests);
      const hasPartitionOptions = partitions.length > 1;

      return (
        <View style={styles.iconContainer}>
          {hasNotes ? (
            <View testID="has-note-icon">
              <NoteIcon
                height={16}
                stroke={theme.sk.color.icon.secondary}
                width={16}
              />
            </View>
          ) : null}
          {hasRequests && canWriteRequest ? (
            <View testID="has-request-icon">
              <SendNoteIcon
                height={16}
                stroke={theme.sk.color.icon.secondary}
                width={16}
              />
            </View>
          ) : null}
          {hasPartitionOptions ? ( // right arrow to show user there's a partition menu afterwards
            <View testID="has-partition-options-icon">
              <ChevronRightIcon
                height={16}
                stroke={theme.sk.color.icon.secondary}
                width={16}
              />
            </View>
          ) : null}
        </View>
      );
    },
    [info, collections, theme.sk.color.icon.secondary, canWriteRequest],
  );

  const renderPartitionRightComponent = useCallback(
    (
      _partition: OrderGuidePartition,
      hasNotes: boolean,
      hasRequests: boolean,
    ) => {
      return (
        <View style={styles.iconContainer}>
          {hasNotes ? (
            <View testID="has-note-icon">
              <NoteIcon
                height={16}
                stroke={theme.sk.color.icon.secondary}
                width={16}
              />
            </View>
          ) : null}
          {hasRequests && canWriteRequest ? (
            <View testID="has-request-icon">
              <SendNoteIcon
                height={16}
                stroke={theme.sk.color.icon.secondary}
                width={16}
              />
            </View>
          ) : null}
        </View>
      );
    },
    [theme.sk.color.icon.secondary, canWriteRequest],
  );

  return (
    <SearchOrderGuideSelectionActionSheet
      info={info}
      onClose={onClose}
      onSelect={handleSelect}
      renderCollectionRightComponent={renderCollectionRightComponent}
      renderPartitionRightComponent={renderPartitionRightComponent}
      skusInOrderGuide={skusInOrderGuide}
      title={i18n.t('add_item_note')}
    />
  );
};

const styles = StyleSheet.create({
  iconContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
});
